import * as React from 'react';
import { useFormApi, useFormState, useFieldState } from "informedv4";
import * as Moment from 'moment';
import { Dropdown } from '../../forms/informedv4/DropdownV4';
import { ConfigFieldTextBox } from '../../forms/informedv4/ConfigFieldTextBoxV4';
import { ConfigFieldTextBoxStaticPrefix } from '../../forms/informedv4/ConfigFieldTextBoxStaticPrefixV4';
import { ConfigFieldDropdown } from '../../forms/informedv4/ConfigFieldDropdownV4';
import { ConfigFieldDatePicker } from '../../forms/informedv4/ConfigFieldDatePickerV4';
import { ConfigFieldTextBoxWithTooltip } from '../../forms/informedv4/ConfigFieldTextBoxWithTooltipV4';
import { MaxLength } from '../../forms/validation/MaxLength';
import { Required } from '../../forms/validation/Required';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { SubjectFormModel } from '../SubjectsShared';
import { ConfigurableDataItemIds } from '../../misc/api/ApiConfigurableDataItems';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { Site, SubjectType, Language } from 'webportalclient';
import { useSecurityContext } from '../../Core/SecurityContext';
import { DatePickerAcceptDateRange } from '../../forms/informedv4/DatePickerV4';
import { CalculateBmi } from '../Bmi';
import { CalculateAge } from '../Age';
import { UnitTypeEnum } from '../../misc/api/ApiUnitTypes';

interface SubjectFormBodyProps {
    isEdit: boolean;
    canEdit: boolean;
    sites: Site[];
    onSetLimitedLanguages: React.Dispatch<React.SetStateAction<Language[]>>;
    subjectLimitedLanguages: Language[];
}

export const SubjectFormBody = ({
    isEdit,
    canEdit,
    sites,
    onSetLimitedLanguages,
    subjectLimitedLanguages,
}: SubjectFormBodyProps): JSX.Element => {
    const [subjectTypes, setSubjectTypes] = React.useState<SubjectType[]>([]);

    const [loaded, setLoaded] = React.useState(false);
    const [sitePrefix, setSitePrefix] = React.useState<string>();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const formApi = useFormApi();
    const formState = useFormState();
    const { value: siteId } = useFieldState('SiteId');
    const { genders, smokingStatuses } = useReferenceContextProvider();
    const { languages, study, dataItemConfigurationCollection, populationGroups, studySubjectSetting } = useCurrentStudyConfigurationContext();
    const { currentUser, currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        const loadInitial = async () => {
            const subjectTypesResult = await client.client.getSubjectTypes();
            setSubjectTypes(subjectTypesResult.value);


            setLoaded(true);
        }

        loadInitial();
    }, [currentUser, currentUserPermissions]);

    React.useEffect(() => {
        if (sites.length > 0 && dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixWithSiteNumber) {
            const site = sites.find(s => s.id === formState.values.SiteId);
            if (site) {
                const prefixDelimiter = dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixDelimiter;

                setSitePrefix(site.number + prefixDelimiter);
                if (formApi.getValue('SubjectNumber')) {
                    formApi.setValue('SubjectNumber', (formApi.getValue('SubjectNumber') as string).replace(site.number + prefixDelimiter, ""));
                }
            }
        }
    }, [siteId, sites, dataItemConfigurationCollection]);

    const onHeightWeightBlur = async () => {
        let height = formApi.getValue('Height') as number;
        let weight = formApi.getValue('Weight') as number;
        if (height && weight) {
            var heightItem = dataItemConfigurationCollection.find(x => x.configurableDataItemId == ConfigurableDataItemIds.SubjectHeight);
            var weightItem = dataItemConfigurationCollection.find(x => x.configurableDataItemId == ConfigurableDataItemIds.SubjectWeight);

            if (heightItem.formatId === UnitTypeEnum.Inches) {
                height = height * 2.54;
            }

            if (weightItem.formatId === UnitTypeEnum.Pounds) {
                weight = weight * 0.45359237;
            }

            var bmi = CalculateBmi(height, weight);
            formApi.setValue('BMI', bmi);
        }
    };

    const validateDateOfBirth = (dateStr?: string): string | undefined => {
        if (!dateStr) return undefined;

        const m = Moment(dateStr);
        if (!m.isValid()) return "Please enter a valid date";
        if (m.isAfter(Moment(), "day")) return "Date of birth cannot be in the future";

        return undefined;
    };

    const handleBirthDateBlur = async (dateOfBirth?: string) => {
        formApi.setError("BirthDate", undefined);

        const err = validateDateOfBirth(dateOfBirth);
        if (err) {
            formApi.setError("BirthDate", err);
            return;
        }

        if (dateOfBirth) {
            const age = CalculateAge(new Date(dateOfBirth));
            formApi.setValue('Age', age);
        }
    };

    if (!loaded
        || dataItemConfigurationCollection.length === 0 || !studySubjectSetting
    ) return <LoadingWithMessage message="Loading Subject Form..." />;

    const onSiteChange = () => {
        const siteId = formApi.getValue("SiteId");
        const site = sites.find(s => s.id === siteId);
        if (site) {
            setSitePrefix(site ? site.number : "");
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                onSetLimitedLanguages(languages.filter(x => x.id.endsWith(site.country)))
                if (!subjectLimitedLanguages.some(x => x.id == formState.values.LanguageId))
                    formApi.setValue('LanguageId', undefined);
            }
        } else {
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                onSetLimitedLanguages([])
            } else {
                onSetLimitedLanguages(languages)
            }
        }
    }


    return (
        <>
            <div className="row">
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="select-site">Site</label>
                    <Dropdown
                        name="SiteId"
                        id="SiteId"
                        field="SiteId"
                        disabled={!canEdit}
                        validate={Required}
                        validateOn="submit"
                        onChange={() => onSiteChange()}
                    >
                        <option
                            key={"OptionSiteBlank"}
                            value={[]}
                        />
                        {sites.map(so => (
                            <option
                                key={`Option${so.id}`}
                                value={so.id}
                            >
                                {so.number}
                            </option>
                        ))}
                    </Dropdown>
                </div>
                {
                    dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixWithSiteNumber ?
                        <ConfigFieldTextBoxStaticPrefix
                            id="SubjectNumber"
                            name="SubjectNumber"
                            field="SubjectNumber"
                            type="text" 
                            className="form-group col-md-4 col-sm-4 col-xs-12"
                            siteNumber={sitePrefix}
                            validated={true}
                            required={true}
                            disabled={isEdit}
                            autoComplete="nickname"
                            itemId={ConfigurableDataItemIds.SubjectId}
                        />
                        : <ConfigFieldTextBox
                            name="SubjectNumber"
                            type="text"
                            field="SubjectNumber"
                            id="SubjectNumber"
                            className="form-group col-md-4 col-sm-4 col-xs-12"
                            validated={true}
                            required={true}
                            disabled={isEdit}
                            itemId={ConfigurableDataItemIds.SubjectId}
                        />
                }
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    name="GenderId"
                    id="Gender"
                    field="GenderId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectGender}
                >
                    {
                        genders.map(so => (
                            <option
                                key={`Option${so.Id}`}
                                value={so.Id}
                            >
                                {so.Name}
                            </option>
                        ))
                    }
                </ConfigFieldDropdown>
                <ConfigFieldDatePicker
                    name="BirthDate"
                    id="BirthDate"
                    field="BirthDate"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    onBlur={handleBirthDateBlur}
                    itemId={ConfigurableDataItemIds.SubjectDateOfBirth}
                    placeholder=""
                    acceptDateRange={DatePickerAcceptDateRange.PastOrPresent}
                    disabled={!canEdit}
                />
                <ConfigFieldTextBox
                    field="Age"
                    id="Age"
                    name="Age"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    required={true}
                    disabled={!canEdit}
                    autoComplete="age"
                    type="number"
                    itemId={ConfigurableDataItemIds.SubjectAge}
                />
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    name="PopulationGroupId"
                    id="PopulationGroupName"
                    field="PopulationGroupId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectPopulationGroup}
                >
                    {populationGroups.map(pg => (<option key={`Option${pg.Id}`} value={pg.Id}>{pg.Name}</option>))}
                </ConfigFieldDropdown>
                <ConfigFieldTextBoxWithTooltip
                    name="Race"
                    id="SelfAssessedPopulationGroup"
                    field="Race"
                    labelName="Self Assessed Population Group"
                    tooltipDescription="The subject's self defined population group using the terminology that they prefer to use, this is free text and has no bearing on predicted values"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    validate={MaxLength(40)}
                    required={false}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectPopulationGroup}
                />
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    name="SubjectTypeId"
                    id="SubjectType"
                    field="SubjectTypeId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={false}
                    itemId={ConfigurableDataItemIds.SubjectType}
                    showBlankOption={false}
                    disabled={!canEdit}
                >
                    {subjectTypes.map(so => (
                        <option
                            key={`Option${so.id}`}
                            value={so.id}
                        >
                            {so.name}
                        </option>
                    ))}
                </ConfigFieldDropdown>
                <ConfigFieldDropdown
                    name="SmokerTypeId"
                    id="Smoking-Statuses"
                    field="SmokerTypeId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectSmokerType}
                >
                    {smokingStatuses.map(so => (
                        <option
                            key={`Option${so.Id}`}
                            value={so.Id}
                        >
                            {so.Name}
                        </option>
                    ))}
                </ConfigFieldDropdown>
            </div>
            <div className="row">
                <ConfigFieldTextBox
                    field="Height"
                    id="Height"
                    name="Height"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    required={true}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectHeight}
                    onBlur={onHeightWeightBlur}
                    isInputAddon={true}
                />
                <ConfigFieldTextBox
                    field="Weight"
                    id="Weight"
                    name="Weight"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    validated={true}
                    disabled={!canEdit}
                    itemId={ConfigurableDataItemIds.SubjectWeight}
                    onBlur={onHeightWeightBlur}
                    isInputAddon={true}
                />
                <ConfigFieldTextBox
                    field="BMI"
                    id="BMI"
                    name="BMI"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    disabled={!canEdit}
                    required={true}
                    validated={false}
                    itemId={ConfigurableDataItemIds.SubjectBmi}
                    inputAddon={<>kg/m<sup>2</sup></>}
                />
            </div>
        </>
    );
};
